<script setup lang="ts">
    import { ref, toRefs, computed, watch } from "vue";
    import emitter from "tiny-emitter/instance";

    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { AxiosError } from "axios";
    import { onBeforeMount, onBeforeUnmount } from "vue";
    import { flashWarning } from "@/utils";
    import { NumberValidationRule } from "@/components/forms/inputs/Number/numberValidation";
    import { useStore } from "@/store";
    import { useKForm } from "@/composables/useKForm";

    // TODO Ändra i Vue3
    interface Premedicinering {
        id?: number;
        anestesi: unknown;
        lakemedelsartikel: unknown;
        ordning: number;
        namn: string;
    }

    const store = useStore();

    const props = defineProps<{
        tabId: number; // TODO Ändra i Vue3
        parameters?: {
            premedicineringId: number;
            redigera: boolean;
        };
    }>();

    const { parameters, tabId } = toRefs(props);
    const formRef = ref();
    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    const premedicinering = computed(() =>
        store.getters["operation/premedicinering/premedicineringById"]({
            id: parameters?.value?.premedicineringId,
        })
    );
    const isEditing = computed(() => !!premedicinering.value);
    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData());
    const heading = computed(() => (isEditing.value ? "Redigera premedicinering" : "Lägg till premedicinering"));
    const submitLabel = computed(() => (isEditing.value ? "Spara och stäng" : "Spara och stäng"));

    defineExpose({
        hasUnsavedData,
        tabId,
    });

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: isEditing.value ? "Redigera premedicinering" : "Skapa premedicinering",
        });
    };

    // TODO convert to useTabs in v3
    const closeTab = ({
        override,
        hasUnsavedData,
    }: {
        override?: boolean;
        hasUnsavedData?: boolean;
    } = {}) => {
        if (!override && hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const onCancel = ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        closeTab({ hasUnsavedData });
    };

    const onSubmit = async (premedicinering: unknown) => {
        try {
            if (isEditing.value) {
                await store.dispatch("operation/premedicinering/updatePremedicinering", {
                    premedicinering: premedicinering as Premedicinering,
                });
            } else {
                await store.dispatch("operation/premedicinering/createPremedicinering", {
                    premedicinering: premedicinering as Premedicinering,
                });
            }

            closeTab({ override: true });
        } catch (thrownError) {
            const error = thrownError as AxiosError;

            if (error?.response?.data) {
                handleErrorResponse(error);
                return;
            }

            flashWarning(
                isEditing.value ? "Kunde inte redigera premedicinering" : "Kunde inte lägga till ny premedicinering"
            );
        }
    };

    const fetchFormData = async () => {
        if (!parameters?.value?.premedicineringId) {
            return null;
        }

        return await store.dispatch("operation/premedicinering/getPremedicineringById", {
            id: parameters.value.premedicineringId,
        });
    };

    onBeforeMount(async () => {
        await store.dispatch("operation/premedicinering/addSubscriber");
        setTabName();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("operation/premedicinering/removeSubscriber");
    });

    watch(premedicinering, () => {
        setTabName();
    });
</script>

<template>
    <div class="premedicinering">
        <KForm
            ref="formRef"
            id="premedicinering"
            :use-formulate="true"
            :submit="onSubmit"
            :submit-content="submitLabel"
            cancel-content="Avbryt"
            :form-errors="formErrors"
            :errors="inputErrors"
            :initial-values-getter="fetchFormData"
            @cancel="onCancel"
            class="premedicinering-form"
        >
            <template #header>
                <admin-lock-border />
                <h2>{{ heading }}</h2>
            </template>
            <template #default>
                <div class="form-content">
                    <KText name="namn" label="Namn" validation="required" />

                    <KNumber name="ordning" label="Ordning" :validation="[[NumberValidationRule.Min, 0]]" />

                    <FormulateInput
                        name="anestesi"
                        label="Anestesi"
                        type="autocomplete"
                        url="/operation/kodverk/anestesier/"
                        :hideCode="true"
                    />
                </div>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss"></style>
